import TooltipPositionEnum from '@models/enums/TooltipPositionEnum';
import classNames from 'classnames';
import { PropsWithChildren } from 'react';

interface Props {
  readonly arrowRadiusClassName?: string;
  readonly backgroundColor?: string;
  readonly customArrowClassName?: string;
  readonly customContentClassName?: string;
  readonly customWrapperClassName?: string;
  readonly dataTestId?: string;
  readonly hasArrow?: boolean;
  readonly hasTooltipClass?: boolean;
  readonly label?: string;
  readonly position: TooltipPositionEnum;
}

const Tooltip = ({
  arrowRadiusClassName = 'rounded-sm',
  backgroundColor = 'bg-white',
  customArrowClassName,
  customContentClassName,
  customWrapperClassName,
  dataTestId = 'Tooltip',
  hasArrow,
  hasTooltipClass = true,
  label,
  position,
  children,
}: PropsWithChildren<Props>) => {
  const positionClasses = {
    [TooltipPositionEnum.Top]: 'bottom-3 left-1/2 -translate-x-1/2',
    [TooltipPositionEnum.Bottom]: 'left-1/2 -translate-x-1/2 top-3',
    [TooltipPositionEnum.BottomLeft]:
      'left-1/2 translate-x-[calc(-100%+20px)] top-3',
    [TooltipPositionEnum.Custom]: customContentClassName,
  };

  const isBottomVariant =
    position === TooltipPositionEnum.Bottom ||
    position === TooltipPositionEnum.BottomLeft;
  const isTopVariant = position === TooltipPositionEnum.Top;

  return (
    <div
      className={classNames('absolute z-100 w-full', customWrapperClassName, {
        tooltip: hasTooltipClass,
        'top-0': isTopVariant,
        'bottom-0': isBottomVariant,
      })}
    >
      {hasArrow && (
        <span
          className={classNames(
            '-translate-x-1/2 absolute left-1/2 h-[17px] w-[17px] rotate-45',
            backgroundColor,
            arrowRadiusClassName,
            customArrowClassName,
            {
              'top-2.5': isBottomVariant,
              'bottom-2.5': isTopVariant,
            }
          )}
          data-testid={`${dataTestId}-Arrow`}
        />
      )}

      {children ? (
        <div className={classNames('absolute', positionClasses[position])}>
          {children}
        </div>
      ) : (
        <p
          className={classNames(
            'small-text absolute whitespace-nowrap rounded-full p-1 px-3 text-center text-black-100',
            backgroundColor,
            positionClasses[position]
          )}
          data-testid={dataTestId}
        >
          {label}
        </p>
      )}
    </div>
  );
};

export default Tooltip;
